'use client';

/**
 * ToolTimeoutFailed — "the retry also failed" card of the tool-timeout
 * recovery flow (lib/tool-timeout-view.ts's ToolTimeoutView.FAILED).
 * Weighting FLIPS from ToolTimeoutOffer: leave-details is now the filled
 * primary and retry is demoted to a small quiet secondary — a second
 * consecutive failure means whatever's wrong isn't clearing on its own, so
 * offering retry equal billing again would be misleading.
 *
 * Copy review (governing principle: "Sara speaks in the user's world,
 * never ours" — no internal service names, no mechanism nouns like
 * "retry"/"attempt"/"request" as things that happen to the user): the body
 * used to say "That service is down on our side — retrying won't help
 * right now", which named an internal service AND used "retrying" as a
 * bare noun. Rewritten below without either, keeping the same "not your
 * fault" framing and unhurried register — see that string's own comment.
 */
import { Mail, RefreshCw, WifiOff } from 'lucide-react';
import { STANDARD_CARD_TONE, StandardCard } from './primitives/standard-card';

interface ToolTimeoutFailedProps {
  onLeaveDetails: () => void;
  onRetry: () => void;
  /** Backend-computed single source of truth (ToolTimeoutOfferEvent.
   *  retry_available) for whether a retry should be offered at all — false
   *  past the backend's retry cap. Hides "Try once more" ENTIRELY rather
   *  than disabling it: Sara's own spoken/text copy already tells the user
   *  retrying won't help right now, so a present-but-dead button would
   *  contradict her. Defaults to true so existing call sites (and this
   *  component's own earlier tests) that never pass it keep today's
   *  two-action rendering unchanged. */
  retryAvailable?: boolean;
  /** Session-recovery Finding 5: set when the last "Try once more" tap
   *  couldn't even SEND the retry_tool_call frame (the socket wasn't open)
   *  — see ToolTimeoutOffer's identical prop for the full rationale.
   *  `null`/undefined renders nothing — the ordinary "we usually reply by
   *  email" footnote shows instead. */
  error?: string | null;
}

export function ToolTimeoutFailed({
  onLeaveDetails,
  onRetry,
  retryAvailable = true,
  error,
}: ToolTimeoutFailedProps) {
  return (
    <StandardCard
      tone={STANDARD_CARD_TONE.NEUTRAL}
      icon={<WifiOff aria-hidden="true" className="size-[15px]" />}
      eyebrow="STILL UNREACHABLE"
      title="Still can't reach it"
      actions={{
        primary: {
          label: 'Leave my details',
          onClick: onLeaveDetails,
        },
        secondary: retryAvailable
          ? {
              label: 'Try once more',
              icon: <RefreshCw aria-hidden="true" className="size-[12px]" />,
              variant: 'quiet',
              onClick: onRetry,
            }
          : undefined,
        footnote: error ? (
          <span role="alert" style={{ color: '#b3261e' }}>
            {error}
          </span>
        ) : (
          <>
            <Mail aria-hidden="true" className="size-[13px]" />
            We usually reply by email within a few hours.
          </>
        ),
      }}
    >
      {/* Copy review: no "service", no "retrying" as a noun — see this
          file's header comment. Same "not your fault" framing, same
          unhurried register, and reuses "back up and running" verbatim
          from tool-timeout-callback.tsx's own rewrite for the identical
          underlying event (see that file's own copy-review note). */}
      This still isn&apos;t coming through on our end, so trying again right now
      won&apos;t help. Let me take your details — a live agent will follow up
      the moment we&apos;re back up and running.
    </StandardCard>
  );
}
